import React, { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, useIsMultiPortfolioTenant } from '../../common/hooks';
import { Typography, BasicButton } from '../../components/atoms';
import { Box, DialogProps, useTheme } from '@mui/material';
import { TableWithCustomRender } from '../../components/molecules';
import { getLabelFromAccountType, getLastUpdateDateFromInvestmentAccounts } from '../../utils';
import { updateCurrentStep } from '../../features/client';
import { selectUnsavedChangeDate } from '../../features/unsaved-plan-changes';
import { EWS_TERM_TAB_KEY_MAP, TERM_LOCALIZATION, STATUSVARIANTS, TermTabType } from '../../common/constants';
import dayjs from 'dayjs';
import UpdateFundingGoalsStepModal, {
    AmountInput,
    InfoBanner,
    TABLE_ROW_HEIGHT,
    TableFrame,
    amountCellSx,
    labelTextSx,
    stepButtonSx,
    tableBodyCellSx,
    tableHeaderCellSx
} from './UpdateFundingGoalsStepModal';

type UpdateFundingnGoalsProps = {
    isOpen: boolean;
    onClose: () => void;
    // eslint-disable-next-line no-unused-vars
    onSubmit: (isEdited: boolean, values: any) => void;
    investmentAccounts: any[];
    selectedTermIndex: TermTabType;
    transitionDuration?: DialogProps['transitionDuration'];
};

type ColumnAlign = 'left' | 'center' | 'right' | 'inherit' | 'justify';

const UpdateFunding: React.FC<UpdateFundingnGoalsProps> = ({
    isOpen,
    onClose,
    onSubmit,
    investmentAccounts,
    selectedTermIndex,
    transitionDuration
}) => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();

    const theme = useTheme();
    const accountSourcesRef = useRef<any[]>([]);
    const language = i18n.language;
    const { orgInfo } = useAppSelector((state) => state.global);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const investmentAccountType = orgInfo?.investmentAccountType?.[language] || [];
    const globalData = adviceGoals?.global as any;
    const isCompletePlan = globalData?.status === STATUSVARIANTS.COMPLETE;
    const unsavedChangeDate = useAppSelector((state) =>
        selectUnsavedChangeDate(state, 'accounts', EWS_TERM_TAB_KEY_MAP[selectedTermIndex])
    );
    const planLastUpdateDate = globalData?.lastUpdateDate ? dayjs(globalData.lastUpdateDate).format('DD MMM YYYY') : '';
    const accountsLastUpdateDate = getLastUpdateDateFromInvestmentAccounts(investmentAccounts || []);
    // AEX-3724: an account change in this term that isn't saved with the plan yet shows its own date. Otherwise
    // complete plans show the plan's last update date, incomplete ones the latest account update;
    // if that date is missing, fall back to the other instead of leaving the date blank.
    const lastUpdateDate = unsavedChangeDate
        ? dayjs(unsavedChangeDate).format('DD MMM YYYY')
        : isCompletePlan
          ? planLastUpdateDate || accountsLastUpdateDate
          : accountsLastUpdateDate || planLastUpdateDate;

    useEffect(() => {
        accountSourcesRef.current = investmentAccounts;
    }, [investmentAccounts]);

    const checkIsSourceEdited = (original: any[], current: any[]): boolean => {
        const isSameValue = (left: any, right: any): boolean => {
            if (left === right) return true;
            if (Array.isArray(left) && Array.isArray(right)) {
                if (left.length !== right.length) return false;
                return left.every((item, i) => isSameValue(item, right[i]));
            }
            if (left && right && typeof left === 'object' && typeof right === 'object') {
                const leftKeys = Object.keys(left);
                const rightKeys = Object.keys(right);
                if (leftKeys.length !== rightKeys.length) return false;
                return leftKeys.every(
                    (key) => Object.prototype.hasOwnProperty.call(right, key) && isSameValue(left[key], right[key])
                );
            }
            return false;
        };
        if (original.length !== current.length) return true;
        return original.some((item, i) => !isSameValue(item, current[i]));
    };

    const accountHeaders: {
        label: string;
        key: string;
        align: ColumnAlign;
        width: string;
        headerSx?: any;
        cellSx?: any;
        // eslint-disable-next-line no-unused-vars
        render?: (value: any, row: any) => React.ReactNode;
    }[] = [
        {
            label: t('NAME'),
            key: 'name',
            align: 'left',
            // Figma column widths: Type and Account Balance are fixed, Name takes the rest.
            width: 'auto',
            headerSx: tableHeaderCellSx,
            cellSx: tableBodyCellSx,
            // eslint-disable-next-line no-unused-vars
            render: (value: any, row: any) => {
                let subtitle =
                    row?.accountHolder == 'primary' ? 'Client' : row?.accountHolder === 'joint' ? 'Joint' : 'Co-Client';
                subtitle = t(subtitle.toUpperCase());
                if (row?.recurringContributionsStartDate && row?.recurringContributionsStartDate != '') {
                    const startFmt = dayjs(row?.recurringContributionsStartDate, 'DD-MM-YYYY').format('MMM YYYY');
                    const endFmt = dayjs(row?.recurringContributionsEndDate, 'DD-MM-YYYY').format('MMM YYYY');
                    subtitle += ` | ${startFmt} - ${endFmt}`;
                }

                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                            label={row['accountName']}
                            variant="body.small"
                            sx={{
                                ...labelTextSx,
                                color: theme.palette.semantic.text.default
                            }}
                        />
                        <Typography
                            label={subtitle}
                            variant="body.small"
                            sx={{
                                ...labelTextSx,
                                color: theme.palette.semantic.text.muted
                            }}
                        />
                    </Box>
                );
            }
        },
        {
            label: t('TYPE'),
            key: 'accountType',
            align: 'left',
            width: theme.spacing(30),
            headerSx: tableHeaderCellSx,
            cellSx: {
                ...tableBodyCellSx,
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '19px',
                color: theme.palette.semantic.text.default
            },
            render: (value: any) => getLabelFromAccountType(value, investmentAccountType)
        },
        {
            label: `${t('ACCOUNT_BALANCE')}`,
            key: 'amount',
            align: 'right',
            width: theme.spacing(35),
            headerSx: tableHeaderCellSx,
            cellSx: amountCellSx,
            render: (value: any, row: any) => {
                return (
                    <AmountInput
                        onChange={(value: number | string) => {
                            row.amount = value;
                            accountSourcesRef.current = accountSourcesRef.current.map((acc) =>
                                acc.accountId === row.accountId ? { ...acc, amount: value } : acc
                            );
                        }}
                        value={value}
                    />
                );
            }
        }
    ];

    // const handleSubmit = (e: React.FormEvent) => {
    //     e.preventDefault();
    //     return;
    // };

    return (
        <UpdateFundingGoalsStepModal
            open={isOpen}
            onClose={onClose}
            transitionDuration={transitionDuration}
            title={
                    isMultiPortfolioTenant
                    ? t('UPDATE_FUNDING_SOURCES_TITLE', { selected_term: t(TERM_LOCALIZATION[selectedTermIndex]) })
                    : t('UPDATE_FUNDING_SOURCES_TITLE_DEFAULT')
            }
            subTitle={t('UPDATE_FUNDING_POPUP_SUBTITLE', { last_update_date: lastUpdateDate })}
            actions={
                <BasicButton
                    onClick={() => {
                        onSubmit(checkIsSourceEdited(investmentAccounts, accountSourcesRef.current), {
                            accounts: accountSourcesRef.current
                        });
                    }}
                    variant="contained"
                    disableElevation
                    sx={stepButtonSx}
                >
                    {t('CLIENT_ONBOARDING_CONTD_BTN')}
                </BasicButton>
            }
        >
            <InfoBanner>
                <Trans
                    i18nKey={'UPDATE_FUNDING_SOURCES_INFO_TEXT'}
                    components={{
                        strong: <strong />,
                        profileLink: (
                            <a
                                href="#"
                                onClick={async (e) => {
                                    e.preventDefault();
                                    await dispatch(updateCurrentStep(0));
                                    onClose();
                                }}
                                style={{
                                    fontWeight: 600,
                                    lineHeight: '20px',
                                    color: theme.palette.semantic.int.selected,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    whiteSpace: 'nowrap'
                                }}
                            />
                        )
                    }}
                />
            </InfoBanner>
            <TableFrame>
                <TableWithCustomRender
                    headers={accountHeaders}
                    dataSource={accountSourcesRef.current}
                    variant="ftds"
                    embedded="all"
                    rowHeight={TABLE_ROW_HEIGHT}
                />
            </TableFrame>
        </UpdateFundingGoalsStepModal>
    );
};

export default UpdateFunding;
